import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "What services do you offer?",
  "Tell me about Melanie",
  "How do I book a call?",
  "What does it cost?",
];

const GREETING =
  "Hi, I'm ElevateBot — an AI assistant for Activate Then Elevate (Melanie always follows up personally). Ask me about our services, or I can help you book a free consultation. 👋";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(`web:${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-elevatebot", openHandler);
    return () => window.removeEventListener("open-elevatebot", openHandler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: ?/, "");
          if (line === "[DONE]") continue;
          if (line.length) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + line,
              };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I hit a snag. Please call or text 843-731-5155.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="live-chatbot-toggle-button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full flex items-center justify-center text-white shadow-2xl"
        style={{ background: "linear-gradient(135deg,#FF4FD8,#E933C6 55%,#8B5CF6)", boxShadow: "0 10px 40px -8px rgba(233,51,198,0.7)" }}
        aria-label="Chat with ElevateBot"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare size={23} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="live-chatbot-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[24rem] h-[32rem] rounded-3xl overflow-hidden flex flex-col"
            style={{ background: "#0b0912", border: "1px solid rgba(233,51,198,0.25)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3 border-b border-white/10" style={{ background: "linear-gradient(100deg,rgba(233,51,198,0.15),rgba(139,92,246,0.12))" }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#E933C6,#8B5CF6)" }}>
                <Sparkles size={17} className="text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold text-white text-sm">ElevateBot</div>
                <div className="text-[11px] text-fuchsia-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> AI assistant · online
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                      m.role === "user"
                        ? "text-white rounded-br-sm"
                        : "text-slate-200 bg-white/5 border border-white/10 rounded-bl-sm"
                    }`}
                    style={m.role === "user" ? { background: "linear-gradient(100deg,#E933C6,#8B5CF6)" } : {}}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <TypingDots /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick chips */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    className="text-[12px] px-3 py-1.5 rounded-full bg-fuchsia-500/10 text-fuchsia-200 border border-fuchsia-500/20 hover:bg-fuchsia-500/20 transition"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="live-chatbot-input-field"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask anything…"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-fuchsia-400/60"
              />
              <button
                data-testid="live-chatbot-send-button"
                onClick={() => send()}
                disabled={busy}
                className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#E933C6,#8B5CF6)" }}
              >
                <Send size={17} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const TypingDots = () => (
  <span className="inline-flex gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-fuchsia-400"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </span>
);

export default ChatWidget;
